import React, { useState } from 'react';
import type { ContactSubmission, Player } from '@/types';
import { Inbox, Mail, Phone, Trash2, UserPlus, ShieldCheck, CheckCircle2 } from 'lucide-react';

interface RequestsTabProps {
  submissions: ContactSubmission[];
  players: Player[];
  onStatusChange: (submissionId: string, status: ContactSubmission['status']) => void;
  onDelete: (submissionId: string) => void;
  onCreatePlayer: (submission: ContactSubmission) => Promise<void>;
}

const TYPE_STYLES: Record<ContactSubmission['type'], string> = {
  'join-team': 'bg-willard-taupe text-tiger-primary-black',
  question: 'bg-tiger-neutral-200 text-tiger-neutral-700',
  general: 'bg-tiger-neutral-100 text-tiger-neutral-600',
};

const TYPE_LABELS: Record<ContactSubmission['type'], string> = {
  'join-team': 'Join Team',
  question: 'Question',
  general: 'General',
};

const STATUS_OPTIONS: { value: ContactSubmission['status']; label: string; activeClass: string }[] = [
  { value: 'new', label: 'New', activeClass: 'bg-amber-500 text-white' },
  { value: 'read', label: 'Read', activeClass: 'bg-tiger-primary-black text-white' },
  { value: 'responded', label: 'Responded', activeClass: 'bg-green-600 text-white' },
];

export const RequestsTab: React.FC<RequestsTabProps> = ({ submissions, players, onStatusChange, onDelete, onCreatePlayer }) => {
  const [creating, setCreating] = useState<string | null>(null);

  const matchedPlayer = (s: ContactSubmission) =>
    players.find(
      (p) =>
        p.removed !== true &&
        ((p.email && p.email.toLowerCase() === s.email.toLowerCase()) ||
          (s.usbcId && p.usbcId && p.usbcId === s.usbcId))
    );

  const handleCreate = async (s: ContactSubmission) => {
    setCreating(s.id);
    try {
      await onCreatePlayer(s);
    } finally {
      setCreating(null);
    }
  };

  if (submissions.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-tiger-lg p-6">
        <div className="text-center py-12">
          <Inbox className="w-16 h-16 text-tiger-neutral-300 mx-auto mb-4" />
          <p className="text-tiger-neutral-600">No contact requests yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {submissions.map(submission => (
        <div
          key={submission.id}
          className={`bg-white rounded-2xl shadow-tiger-lg p-6 ${
            submission.status === 'new' ? 'border-l-4 border-willard-taupe' : ''
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display font-black text-lg text-tiger-primary-black">
                  {submission.name}
                </h3>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-bold ${TYPE_STYLES[submission.type] || TYPE_STYLES.general}`}
                >
                  {TYPE_LABELS[submission.type] || submission.type}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-tiger-neutral-600 flex-wrap">
                <a
                  href={`mailto:${submission.email}`}
                  className="flex items-center gap-1 hover:text-tiger-primary-black transition-colors underline"
                >
                  <Mail className="w-4 h-4" />
                  {submission.email}
                </a>
                {submission.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {submission.phone}
                  </span>
                )}
              </div>
            </div>
            <span className="text-sm text-tiger-neutral-500 whitespace-nowrap">
              {submission.submittedAt.toLocaleDateString()}
            </span>
          </div>

          <p className="font-semibold text-tiger-primary-black mb-1">{submission.subject}</p>
          <p className="text-tiger-neutral-700 whitespace-pre-wrap mb-4">{submission.message}</p>

          {submission.type === 'join-team' && (
            <div className="p-3 mb-4 bg-willard-grey-50 border border-tiger-neutral-200 rounded-lg space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {submission.usbcId ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-willard-taupe text-tiger-primary-black text-xs font-bold">
                    <ShieldCheck className="w-3.5 h-3.5" /> USBC {submission.usbcId}
                  </span>
                ) : (
                  <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
                    No USBC #, new to league bowling
                  </span>
                )}
                {submission.grade && (
                  <span className="px-2.5 py-1 rounded-full bg-tiger-neutral-200 text-tiger-neutral-700 text-xs font-bold">
                    Grade {submission.grade}
                  </span>
                )}
                {submission.experience && (
                  <span className="px-2.5 py-1 rounded-full bg-tiger-neutral-200 text-tiger-neutral-700 text-xs font-bold capitalize">
                    {submission.experience}
                  </span>
                )}
                {submission.league && (
                  <span className="px-2.5 py-1 rounded-full bg-tiger-neutral-200 text-tiger-neutral-700 text-xs font-bold">
                    {submission.league}
                  </span>
                )}
              </div>
              {matchedPlayer(submission) ? (
                <p className="flex items-center gap-2 text-sm text-green-700 font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> Player card exists ({matchedPlayer(submission)!.name}).
                  Manage from the Roster or Onboarding tab.
                </p>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => handleCreate(submission)}
                    disabled={creating === submission.id}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-tiger-primary-black text-white text-sm font-bold rounded-lg hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                    {creating === submission.id ? 'Creating…' : 'Create player card'}
                  </button>
                  <span className="text-sm text-tiger-neutral-600">
                    Prefills name, email{submission.usbcId ? ', USBC #' : ''}
                    {submission.grade ? ', grade' : ''}, then reply with{' '}
                    <span className="font-semibold">willardbowling.com/signup</span> so they can claim it
                    (same email). Leagues import automatically once the USBC # is on the card.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              {STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => onStatusChange(submission.id, option.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    submission.status === option.value
                      ? option.activeClass
                      : 'bg-tiger-neutral-100 text-tiger-neutral-600 hover:bg-tiger-neutral-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => onDelete(submission.id)}
              title="Delete request"
              className="p-2 text-tiger-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
